import pLimit from 'p-limit';

import * as Metrics from 'metrics/Metrics';
import * as Logging from 'util/Logging';
import {withExponentialBackoff} from 'util/requestWithExponentialBackoff';

import type {
  ActionableGoogleMimeBehavior,
  DirectDownloadOutputFileInfo,
  DownloadResult,
  DownloadSingleGoogleDriveFileInstructions,
  ExportOutputFileInfo,
  GoogleDriveIssue,
  GoogleMimeTypeBehavior,
  InputFileInfo,
} from './ValidateAndDownloadGoogleDriveFilesTypes';
import {
  createInvalidFilePickedDownloadResult,
  createUnableToDirectDownloadFileIssue,
  createUnableToExportFileIssue,
  GoogleDriveUnreachableIssue,
} from './ValidateAndDownloadGoogleDriveFilesTypes';

const GOOGLE_MIME_TYPE_PREFIX = 'application/vnd.google';
const GOOGLE_DRIVE_DOWNLOAD_FAILURE_METRIC = 'notebook.gdrive_picker.download_failure.redirect';

// Closed set of failure reasons recorded in the failure log. Keep this a finite union
// (not `string`) so future edits can't accidentally record a high-cardinality value
// (e.g. a raw statusText or error message) under this dimension.
type DownloadFailureReason = 'picker_not_loaded' | 'invalid_file' | 'unreachable' | 'http_error';

// Reasons that indicate a broken assumption rather than a transient/environmental
// failure, so they warrant Sentry visibility (Logging.error). `picker_not_loaded`
// means validateAndDownloadGoogleDriveFiles was called before the Google Picker API
// script loaded — a caller/sequencing precondition violation that throws
// GooglePickerAPIScriptNotLoadedError and keeps the flow permanently broken until
// fixed, not something the user or environment can recover from. Every other reason
// is transient or expected (invalid file picked, Google unreachable, non-OK HTTP) and
// stays at `warn` so we don't flood Sentry with noise we can't act on. Mirrors the
// ERROR_LEVEL_LOAD_FAILURE_REASONS convention in the sibling buildGoogleDrivePicker.ts.
const ERROR_LEVEL_DOWNLOAD_FAILURE_REASONS: ReadonlySet<DownloadFailureReason> = new Set([
  'picker_not_loaded',
]);

// The maximum number of download/export requests we want to wait for in parallel. We are capping this to reduce the number of concurrent requests to Google (to avoid things like rate limiting)
export const CONCURRENCY_LIMIT = 3;

const GOOGLE_MIME_TYPES: Record<string, GoogleMimeTypeBehavior> = {
  // Not allowed

  // Third party shortcut
  'application/vnd.google-apps.drive-sdk': {action: 'not_allowed'},
  // Google Form
  'application/vnd.google-apps.form': {action: 'not_allowed'},
  // Google Fusion Tables
  'application/vnd.google-apps.fusiontable': {action: 'not_allowed'},
  // Gmail layout template
  'application/vnd.google-apps.mail-layout': {action: 'not_allowed'},
  // Google My Maps
  'application/vnd.google-apps.map': {action: 'not_allowed'},
  // Google Apps Script
  'application/vnd.google-apps.script': {action: 'not_allowed'},
  // Drive shortcut
  'application/vnd.google-apps.shortcut': {action: 'not_allowed'},
  // Google Sites
  'application/vnd.google-apps.site': {action: 'not_allowed'},
  // Google Vids -- a video project in the Google Vids video editor
  'application/vnd.google-apps.vid': {action: 'not_allowed'},

  // Direct download -- some of these may not be downloadable; if the download fails, we'll return an error to the user

  // Google internal pointer tag for audio -- most likely can't download this, but we will attempt to
  'application/vnd.google-apps.audio': {
    action: 'direct',
    outMime: 'application/octet-stream',
    extension: '.bin',
  },
  // Google tag for generic file -- may be able to download this, may not
  'application/vnd.google-apps.file': {
    action: 'direct',
    outMime: 'application/octet-stream',
    extension: '.bin',
  },
  // Google internal pointer tag for photo -- most likely can't download this, but we will attempt to
  'application/vnd.google-apps.photo': {
    action: 'direct',
    outMime: 'application/octet-stream',
    extension: '.bin',
  },
  // Google internal pointer tag for video -- most likely can't download this, but we will attempt to
  'application/vnd.google-apps.video': {
    action: 'direct',
    outMime: 'application/octet-stream',
    extension: '.bin',
  },
  // Gemini Gem -- a template for Gemini conversations
  'application/vnd.google-gemini.gem': {action: 'direct', outMime: 'application/json', extension: '.json'},
  // Unknown type -- may be able to download this, may not
  'application/vnd.google-apps.unknown': {
    action: 'direct',
    outMime: 'application/octet-stream',
    extension: '.bin',
  },

  // Export

  // Google Docs exports to PDF
  'application/vnd.google-apps.document': {action: 'export', outMime: 'application/pdf', extension: '.pdf'},
  // Google Drawing exports to PDF
  'application/vnd.google-apps.drawing': {action: 'export', outMime: 'application/pdf', extension: '.pdf'},
  // Google Slides exports to .pptx
  'application/vnd.google-apps.presentation': {
    action: 'export',
    outMime: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    extension: '.pptx',
  },
  // Google Sheets exports to .xlsx
  'application/vnd.google-apps.spreadsheet': {
    action: 'export',
    outMime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    extension: '.xlsx',
  },
  // Google Jamboard exports to PDF -- most likely users won't even have a jamboard file because it's deprecated
  'application/vnd.google-apps.jam': {action: 'export', outMime: 'application/pdf', extension: '.pdf'},
};

function getActionableGoogleMimeBehavior(mimeType: string): ActionableGoogleMimeBehavior | undefined {
  if (!Object.hasOwn(GOOGLE_MIME_TYPES, mimeType)) {
    return undefined;
  }
  const behavior = GOOGLE_MIME_TYPES[mimeType];
  if (behavior.action === 'not_allowed') {
    return undefined;
  }
  return behavior;
}

function logDownloadFailure(
  message: string,
  tags: {reason: DownloadFailureReason; action?: DownloadSingleGoogleDriveFileInstructions['action']},
  extra: Record<string, unknown> = {}
): void {
  // Fire an UNTAGGED count: metric tags are expensive (computationally and in $;
  // see the Metrics.count docstring) and the failure dimensions (reason, action,
  // status, ...) are better carried in the warn log, which is cheaper and where
  // an on-call would look after the metric fires. This also matches the
  // untagged-count convention in the sibling buildGoogleDrivePicker.ts.
  Metrics.count(GOOGLE_DRIVE_DOWNLOAD_FAILURE_METRIC, 1);
  // Escalate broken-assumption reasons to error (Sentry-visible); everything else is
  // transient/expected and stays at warn. See ERROR_LEVEL_DOWNLOAD_FAILURE_REASONS.
  const log = ERROR_LEVEL_DOWNLOAD_FAILURE_REASONS.has(tags.reason) ? Logging.error : Logging.warn;
  // Fold the reason/action dimensions into the log payload so call sites don't
  // restate them in `extra`; call-site `extra` wins on any key collision.
  log(message, {extra: {...tags, ...extra}});
}

export class GooglePickerAPIScriptNotLoadedError extends Error {
  constructor() {
    super('Google Picker API script must be loaded before calling validateAndDownloadGoogleDriveFiles');
    this.name = 'GooglePickerAPIScriptNotLoadedError';
  }
}

/**
 * Generates output filename for direct download of Google mime-type files.
 * Keeps the base of inputFileName the same, but if an extension already exists, overwrite it with outputFileExtension (otherwise just add outputFileExtension)
 *
 * Note: we are assuming that whatever stuff follows the last period in the filename is the original file extension, and
 * we overwrite that with the new file extension. This may not always be true--for example, a file could be named "doc.v2", in
 * which case we strip the v2 and replace it with the new file extension. This is a tradeoff we purposefully made to avoid having
 * to import the mime-types library to check if there is a valid file extension in the original name.
 */
export function generateOutputFilenameForDirectDownloadOfGoogleMimeFiles(
  inputFileName: string,
  outputFileExtension: string
): string {
  return inputFileName.includes('.')
    ? inputFileName.replace(/\.[^.]+$/, outputFileExtension)
    : inputFileName + outputFileExtension;
}

export async function downloadGoogleDriveFile(
  accessToken: string,
  instructions: DownloadSingleGoogleDriveFileInstructions
): Promise<DownloadResult> {
  // Percent-encode the two caller-supplied values interpolated into the Google API
  // URL so URL-significant characters (?, &, #, /, spaces, ...) are treated as data,
  // not as path/query syntax. `inputFileId` lands in the path and `outputFileMimeType`
  // in the query string; without encoding a value like `foo?alt=json` could tamper
  // with the request shape.
  //
  // Encoding alone is sufficient here — we deliberately do NOT add a regex/allow-list
  // validation of the fileId. Neither interpolated value is free user input:
  //   - `inputFileId` (the only Picker-supplied value that reaches the URL) is the id
  //     from the trusted Google Picker `DocumentObject`, null-checked in
  //     validateAndDownloadGoogleDriveFiles before this function is ever reached.
  //   - `outputFileMimeType` only enters the URL on the export path, and there it is
  //     drawn from our own GOOGLE_MIME_TYPES allow-list, not from the Picker. On the
  //     direct-download path the mimeType never enters the URL at all.
  // A syntactic whitelist would add no security value over encoding and risks
  // rejecting legitimate Google ids.
  const googleApiUrl =
    instructions.action === 'direct'
      ? `https://www.googleapis.com/drive/v3/files/${encodeURIComponent(instructions.inputFileInfo.inputFileId)}?alt=media`
      : `https://www.googleapis.com/drive/v3/files/${encodeURIComponent(instructions.inputFileInfo.inputFileId)}/export?mimeType=${encodeURIComponent(instructions.outputFileInfo.outputFileMimeType)}`;

  const {response, error} = await withExponentialBackoff<GoogleDriveIssue>(
    () =>
      fetch(googleApiUrl, {
        headers: {Authorization: `Bearer ${accessToken}`},
      }),
    {
      errorWhenRetriesExhausted: GoogleDriveUnreachableIssue,
    }
  );

  if (error != null) {
    logDownloadFailure(
      'GDrive download failed — Google Drive unreachable',
      {reason: 'unreachable', action: instructions.action},
      {retriesExhausted: true}
    );
    return {fileId: instructions.inputFileInfo.inputFileId, file: null, error};
  }

  // If there was a non-retryable http error code exporting the file, return an error
  if (!response.ok) {
    const issue =
      instructions.action === 'direct'
        ? await createUnableToDirectDownloadFileIssue(instructions.inputFileInfo, response)
        : await createUnableToExportFileIssue(
            instructions.inputFileInfo,
            instructions.outputFileInfo,
            response
          );

    logDownloadFailure(
      'GDrive download failed — non-OK HTTP response',
      {reason: 'http_error', action: instructions.action},
      {
        // The exact numeric status is logged here, so slicing 4xx vs 5xx (or any
        // specific code) is a log query — no need for separate coarse reasons.
        status: response.status,
        statusText: response.statusText,
        // Google's freeform error *message* can embed a file id / email on some 4xx
        // (403/404), so it's only safe to log on the generic 400. The structured
        // *reason* enum (googleErrorReason) never embeds identifiers, so log it on
        // every status — it's what disambiguates a 4xx sub-case (permission vs
        // rate-limit vs not-exportable) that a bare status code can't.
        ...(response.status === 400 ? {googleError: issue.googleErrorMessage} : {}),
        ...(issue.googleErrorReason != null ? {googleErrorReason: issue.googleErrorReason} : {}),
        // On the export path, include the input/output mimeTypes (both from our own
        // GOOGLE_MIME_TYPES allow-list, so no PII) so on-call can see which file-type
        // conversion failed alongside reason/action/status.
        ...(instructions.action === 'export'
          ? {
              inputFileMimeType: instructions.inputFileInfo.inputFileMimeType,
              outputFileMimeType: instructions.outputFileInfo.outputFileMimeType,
            }
          : {}),
      }
    );

    return {
      fileId: instructions.inputFileInfo.inputFileId,
      file: null,
      error: issue,
    };
  }

  const blob = await response.blob();

  return {
    fileId: instructions.inputFileInfo.inputFileId,
    file: new File([blob], instructions.outputFileInfo.outputFileName, {
      type: instructions.outputFileInfo.outputFileMimeType,
    }),
    error: null,
  };
}

/**
 * Generator function that downloads Google drive files (either by a direct download or an export to a different filetype).
 * At each step, returns a DownloadResult, which contains either a File or an error object of type GoogleDriveIssue.
 *
 * @param documentInfos - list of information on the documents we want to either direct download or export
 * @param accessToken - the Google oAuth access token for this user
 * @param concurrencyLimit - the maximum number of download/export requests we want to wait for in parallel.
 *   We are capping this to ensure we don't have too many concurrent requests hitting Google at once (to prevent things like rate limiting).
 */
export async function* validateAndDownloadGoogleDriveFiles(
  documentInfos: Array<google.picker.DocumentObject>,
  accessToken: string,
  concurrencyLimit: number = CONCURRENCY_LIMIT
): AsyncGenerator<DownloadResult> {
  if (typeof google === 'undefined' || !google.picker?.Document) {
    logDownloadFailure('GDrive download aborted — Picker API script not loaded', {
      reason: 'picker_not_loaded',
    });
    throw new GooglePickerAPIScriptNotLoadedError();
  }

  // Validate that concurrency limit is a positive number. If its not, fall back to default CONCURRENCY_LIMIT
  if (concurrencyLimit <= 0) {
    concurrencyLimit = CONCURRENCY_LIMIT;
  }

  const limiter = pLimit(concurrencyLimit);

  // Map of all of the promises to download/export files that we will kick off concurrently (limited by CONCURRENCY_LIMIT)
  const pendingFileRetrievalTasks = new Map<number, Promise<{taskId: number; value: DownloadResult}>>();

  let taskId: number = 0;

  // Loop through all documentInfos. Yield a DownloadResult with an error description if an invalid file is picked. Otherwise, construct
  // the promise to download or export the file, and add it to pendingFileRetrievalTasks.

  for (const documentInfo of documentInfos) {
    const fileName = documentInfo[google.picker.Document.NAME];
    const fileId = documentInfo[google.picker.Document.ID];
    const mimeType = documentInfo[google.picker.Document.MIME_TYPE];

    if (fileId == null) {
      logDownloadFailure(
        'GDrive download skipped — invalid picked file',
        {reason: 'invalid_file'},
        {missingField: 'id'}
      );
      yield createInvalidFilePickedDownloadResult(
        '',
        fileName
          ? `File "${fileName}" is invalid and can't be uploaded. Try a different file`
          : "A selected file is invalid and can't be uploaded."
      );
      continue;
    }
    if (fileName == null) {
      logDownloadFailure(
        'GDrive download skipped — invalid picked file',
        {reason: 'invalid_file'},
        {missingField: 'name'}
      );
      yield createInvalidFilePickedDownloadResult(
        fileId,
        `File with id "${fileId}" is invalid and can't be uploaded. Try a different file`
      );
      continue;
    }
    if (mimeType == null) {
      logDownloadFailure(
        'GDrive download skipped — invalid picked file',
        {reason: 'invalid_file'},
        {missingField: 'mimeType'}
      );
      yield createInvalidFilePickedDownloadResult(
        fileId,
        `File "${fileName}" is invalid and can't be uploaded. Try a different file`
      );
      continue;
    }

    const inputFileInfo: InputFileInfo = {
      inputFileId: fileId,
      inputFileMimeType: mimeType,
      inputFileName: fileName,
    };

    let fileRetrievalTask;

    if (mimeType.startsWith(GOOGLE_MIME_TYPE_PREFIX)) {
      const behavior = getActionableGoogleMimeBehavior(mimeType);
      if (behavior === undefined) {
        const hasKnownGoogleMimeType = Object.hasOwn(GOOGLE_MIME_TYPES, mimeType);
        const missingField = hasKnownGoogleMimeType ? 'disallowed_type' : 'unrecognized_type';
        logDownloadFailure(
          'GDrive download skipped — invalid picked file',
          {reason: 'invalid_file'},
          {missingField}
        );
        const userDescription = hasKnownGoogleMimeType
          ? `File "${fileName}" has a type that is not allowed for upload`
          : `File "${fileName}" has an unrecognized type for upload`;
        yield createInvalidFilePickedDownloadResult(fileId, userDescription);
        continue;
      }

      if (behavior.action === 'direct') {
        const outputFileInfo: DirectDownloadOutputFileInfo = {
          outputFileName: generateOutputFilenameForDirectDownloadOfGoogleMimeFiles(
            fileName,
            behavior.extension
          ),
          outputFileMimeType: behavior.outMime,
        };

        fileRetrievalTask = () =>
          downloadGoogleDriveFile(accessToken, {
            action: 'direct',
            inputFileInfo,
            outputFileInfo,
          });
      } else {
        // Action is export
        // Output file name should just be the current filename with the output file extension appended
        const outputFileInfo: ExportOutputFileInfo = {
          outputFileName: fileName + behavior.extension,
          outputFileMimeType: behavior.outMime,
          outputFileExtension: behavior.extension,
        };

        fileRetrievalTask = () =>
          downloadGoogleDriveFile(accessToken, {
            action: 'export',
            inputFileInfo,
            outputFileInfo,
          });
      }
    } else {
      // For all non-Google mime types, attempt to direct download them, keeping filename and mimetype the same
      const outputFileInfo: DirectDownloadOutputFileInfo = {
        outputFileName: fileName,
        outputFileMimeType: mimeType,
      };

      fileRetrievalTask = () =>
        downloadGoogleDriveFile(accessToken, {
          action: 'direct',
          inputFileInfo,
          outputFileInfo,
        });
    }

    const currentTaskId = taskId;
    pendingFileRetrievalTasks.set(
      currentTaskId,
      limiter(fileRetrievalTask).then((value) => ({taskId: currentTaskId, value}))
    );

    taskId += 1;
  }

  // Fire off all file retrieval tasks, yielding them in the order they finish.
  while (pendingFileRetrievalTasks.size > 0) {
    const {taskId, value} = await Promise.race(pendingFileRetrievalTasks.values());
    pendingFileRetrievalTasks.delete(taskId);
    yield value;
  }
}
